import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import RadiusSlider from '../components/RadiusSlider';
import LocationInput from '../components/LocationInput';
import MapView from '../components/MapView';
import './Home.css';

export default function Home() {
    // set up state hooks
    const [searchTerm, setSearchTerm] = useState('');  // term to search for
    const [radius, setRadius] = useState(10); // default rad = 10 km
    const [pharmacies, setPharmacies] = useState([]);
    const [userLocation, setUserLocation] = useState({ lat: 6.688, lng: -1.642 }); // default to Kumasi if geolocation fails
    const [locationName, setLocationName] = useState('Kumasi, Ghana');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Refs so debounced/async callbacks always read the latest values
    const latestRadiusRef = useRef(radius);
    const latestUserLocationRef = useRef({ lat: 6.688, lng: -1.642 });

    // attempt browser geolocation
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    const newLoc = { lat: coords.latitude, lng: coords.longitude };
                    latestUserLocationRef.current = newLoc;
                    setUserLocation(newLoc);
                },
                () => { console.warn('Geolocation failed — using default Kumasi coords'); }
            );
        }
    }, []);

    // when the user submits a new search
    const handleSearch = async (term) => {
        if (!term.trim()) return;   // ignore empty searches
        setSearchTerm(term);
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/search` +
                `?medicine=${encodeURIComponent(term)}` +
                `&lat=${latestUserLocationRef.current.lat}` +
                `&lng=${latestUserLocationRef.current.lng}` +
                `&radius=${latestRadiusRef.current}`
            );
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            const data = await res.json();
            setPharmacies(data.results ?? []);
        } catch (err) {
            console.error('Error fetching pharmacies: ', err);
            setError('Could not reach the server. Please try again.');
            setPharmacies([]);
        } finally {
            setIsLoading(false);
        }
    };

    // debounce ref for slider — fires only after 300 ms of inactivity
    const radiusDebounceRef = useRef(null);

    // live update results when radius is changed
    const handleRadiusChange = (newRadius) => {
        latestRadiusRef.current = newRadius;
        setRadius(newRadius);
        if (searchTerm) {
            clearTimeout(radiusDebounceRef.current);
            radiusDebounceRef.current = setTimeout(() => handleSearch(searchTerm), 300);
        }
    };

    // called by LocationInput after geocoding a typed address
    const handleLocationChange = ({ lat, lng, name }) => {
        latestUserLocationRef.current = { lat, lng };
        setUserLocation({ lat, lng });
        setLocationName(name);
        if (searchTerm) handleSearch(searchTerm);
    };

    return (
        <div className="home-container">
            <div className="left-panel">
                {/* Search controls */}
                <div className="panel-controls">
                    <label className="control-label">Location</label>
                    <LocationInput
                        locationName={locationName}
                        onLocationChange={handleLocationChange}
                        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    />

                    <label className="control-label">Medicine</label>
                    <SearchBar value={searchTerm} onChange={setSearchTerm} onSearch={handleSearch} />

                    <RadiusSlider radius={radius} onChange={handleRadiusChange} />
                    {error && <p className="search-error">{error}</p>}
                </div>

                {/* Info section pushed to bottom */}
                <div className="info-section">
                    <Link to="/" className="info-logo">
                        <img src={process.env.PUBLIC_URL + '/images/logo.png'} alt="MedSearch logo" />
                        <span>MedSearch</span>
                    </Link>

                    <p className="info-text">
                        Search the name of a medicine and MedSearch will identify and highlight nearby pharmacies that
                        stock the drug AND accept Ghana's National Health Insurance (NHIS) card for prescriptions.
                        <br></br>
                        Adjust the search radius to your preference.
                        <br></br>
                        Click on the pharmacy markers&nbsp;
                        <img src={process.env.PUBLIC_URL + '/images/pharm-icon.svg'} alt="Pharmacy icon" className="inline-icon" />
                        &nbsp;to get directions from your location&nbsp;
                        <img src={process.env.PUBLIC_URL + '/images/user-icon.svg'} alt="You icon" className="inline-icon" />.
                    </p>

                    <p className="info-thanks">
                        We're grateful to the Ghana NHIS, UNICEF Ghana, and Nirav Shah for the invaluable support in bringing this project to life.
                    </p>

                    <p className="info-contact">
                        Have feedback or questions? Email:&nbsp;
                        <a href="mailto:medsearchgh@gmail.com">medsearchgh@gmail.com</a>
                    </p>

                    <div className="info-logos">
                        <img src={process.env.PUBLIC_URL + '/images/ghana-flag.png'} alt="Ghana flag" />
                        <img src={process.env.PUBLIC_URL + '/images/nhis-logo.png'} alt="NHIS logo" />
                        <img src={process.env.PUBLIC_URL + '/images/unicef-logo-2.png'} alt="UNICEF logo" />
                        <img src={process.env.PUBLIC_URL + '/images/logo.png'} alt="MedSearch logo" />
                    </div>

                    <p className="info-disclaimer">
                        Currently covering regions in Ghana - more countries soon.
                    </p>
                </div>
            </div>

            <div className="map-panel">
                <div className="map-content">
                    {isLoading && <div className="map-spinner" />}
                    <MapView
                        userLocation={userLocation}
                        pharmacies={pharmacies}
                        searchTerm={searchTerm}
                        radius={radius}
                    />
                </div>
            </div>
        </div>
    );
}
